import {AppLeague, LeagueInfo} from './league';
import {DateService} from './DateService';
import {FanModerator, FanModeratorStatusType} from './fanmoderator';
import {MatchTeam} from '@shared/models/matchTeam';
import {MatchVideo} from '@shared/models/match-video';
import {TeamModerator} from '@shared/models/teamModerator';
import {IStadium} from '@shared/models/stadium';
import {ISportradar} from '@shared/models/sportradar';
import {IVideo} from '@api/models/video/video';
import {IStreamingInfo} from '@api/models/livestream/streaming-info';
import {Event} from '@api/models/event/event';
import {EventStatus, PostGameAndCancelledPhases} from '@api/models/event/event-status';
import {EntityType} from '@lib/models/entity/entity-type';
import {Entity} from '@lib/models/entity/entity';

export class MatchRound {
  nr: number;
  ui_name: string;

  constructor(init?: Partial<MatchRound>) {
    Object.assign(this, init);
  }
}

export class MatchStatus {
  static readonly UPCOMING = new MatchStatus('UPCOMING', 'upcoming', 'Zukünftig');
  static readonly PREGAME = new MatchStatus('PREGAME', 'pregame', 'Zukünftig');

  static readonly RUNNING = new MatchStatus('RUNNING', 'running', null);
  static readonly FH = new MatchStatus('FH', 'fh', null);
  static readonly FH_START = new MatchStatus('FH_START', 'fh_start', null);
  static readonly HTPAUSE = new MatchStatus('HTPAUSE', 'htpause', 'Halbzeit');
  static readonly FH_PAUSE = new MatchStatus('FH_PAUSE', 'fh_pause', 'Halbzeit');
  static readonly HT_RUNNING = new MatchStatus('HT_RUNNING', 'ht_running', null);
  static readonly SH = new MatchStatus('SH', 'sh', null);
  static readonly SH_START = new MatchStatus('SH_START', 'sh_start', null);
  static readonly SHPAUSE = new MatchStatus('SHPAUSE', 'shpause', 'Pause');
  static readonly SH_PAUSE = new MatchStatus('SH_PAUSE', 'sh_pause', 'Pause');
  static readonly OTFH = new MatchStatus('OTFH', 'otfh', 'Verlängerung');
  static readonly ET_FH_START = new MatchStatus('ET_FH_START', 'et_fh_start', 'Verlängerung');
  static readonly OTPAUSE = new MatchStatus('OTPAUSE', 'otpause', 'Pause');
  static readonly ET_FH_PAUSE = new MatchStatus('ET_FH_PAUSE', 'et_fh_pause', 'Pause');
  static readonly OTSH = new MatchStatus('OTSH', 'otsh', 'Verlängerung');
  static readonly ET_SH_START = new MatchStatus('ET_SH_START', 'et_sh_start', 'Verlängerung');

  static readonly PS = new MatchStatus('PS', 'ps', 'Elfmeter');
  static readonly PENALTY_SHOOTOUT = new MatchStatus('PENALTY_SHOOTOUT', 'penalty_shootout', 'Elfmeter');

  static readonly POSTGAME = new MatchStatus('POSTGAME', 'postgame', 'Endstand');
  static readonly ENDED = new MatchStatus('ENDED', 'ended', '');

  static readonly CANCELLED = new MatchStatus('CANCELLED', 'cancelled', 'Abgesagt');
  static readonly CANCEL = new MatchStatus('CANCEL', 'cancel', 'Abgesagt');
  static readonly ABORTED = new MatchStatus('ABORTED', 'aborted', 'Spielabbruch');
  static readonly ABORT = new MatchStatus('ABORT', 'abort', 'Spielabbruch');

  static readonly types: MatchStatus[] = [
    MatchStatus.UPCOMING,
    MatchStatus.PREGAME,

    MatchStatus.RUNNING,
    MatchStatus.FH,
    MatchStatus.FH_START,
    MatchStatus.HTPAUSE,
    MatchStatus.FH_PAUSE,
    MatchStatus.HT_RUNNING,
    MatchStatus.SH,
    MatchStatus.SH_START,
    MatchStatus.SHPAUSE,
    MatchStatus.SH_PAUSE,
    MatchStatus.OTFH,
    MatchStatus.ET_FH_START,
    MatchStatus.OTPAUSE,
    MatchStatus.ET_FH_PAUSE,
    MatchStatus.OTSH,
    MatchStatus.ET_SH_START,

    MatchStatus.PS,
    MatchStatus.PENALTY_SHOOTOUT,

    MatchStatus.POSTGAME,
    MatchStatus.ENDED,

    MatchStatus.CANCELLED,
    MatchStatus.CANCEL,
    MatchStatus.ABORTED,
    MatchStatus.ABORT,
  ];

  static fromValue(value: string): MatchStatus {
    for (let i = 0; i < this.types.length; i++) {
      if (this.types[i].value === value) {
        return this.types[i];
      }
    }

    return null;
  }

  // private to disallow creating other instances of this type
  private constructor(
    private readonly key: string,
    public readonly value: any,
    public readonly uiString: string,
  ) {}

  public stringForType(): string {
    return this.uiString;
  }
}

export class Match {
  teams: {
    home: MatchTeam,
    away: MatchTeam
  };
  awayScore: number;
  date: string;
  homeScore: number;
  id: string;
  leagueid: string;
  league_name: string;
  league: Promise<AppLeague>;
  liveticker_status: string;
  round: MatchRound; // work with getRoundUIName()
  referee: {
    firstname: string,
    lastname: string
  };
  spectators: number;
  sportradar: ISportradar;
  stadium: IStadium;
  league_info: LeagueInfo;
  sex: string;
  status: string;
  statusObject: MatchStatus;
  timestamps: {
    pregame_start: number,
    start: number,
    end: number,
    postgame_end: number,
    date_string: string,
  };
  formattedTime: string = null;
  scores: {
    regular: {
      firsthalf: {end: string},
      secondhalf: {end: string}
    },
    overtime: {
      firsthalf: {end: string},
      secondhalf: {end: string},
      shootout: {end: string}
    }
  };
  streaming_info?: IStreamingInfo;
  fanModeratorHome: FanModerator;
  fanModeratorAway: FanModerator;
  moderatorsHome: TeamModerator[];
  moderatorsAway: TeamModerator[];
  moderationTypeHome: string;
  moderationTypeAway: string;
  videos: MatchVideo;
  videos_native: IVideo[];

  public constructor(init?: Partial<Event>) {
    Object.assign(this, init);

    this.teams.home = new MatchTeam(init.teams.home);
    this.teams.away = new MatchTeam(init.teams.away);

    this.id = (init as any)._id;
    this.date = init.timestamps.start.toString();
    this.leagueid = (init as any).leagueid;
    this.league_name = (init as any).league_name;

    this.liveticker_status = init.liveticker_status;

    this.fanModeratorHome = new FanModerator(init.teams.home.fan_moderator);
    this.fanModeratorAway = new FanModerator(init.teams.away.fan_moderator);

    this.moderationTypeHome = init.teams.home.moderation_type;
    this.moderationTypeAway = init.teams.away.moderation_type;

    this.timestamps = init.timestamps;
    this.formattedTime = this.getTimeFormatted();

    this.setStatus(init.status);

    this.homeScore = init.teams.home.score >= 0 ? init.teams.home.score : undefined;
    this.awayScore = init.teams.away.score >= 0 ? init.teams.away.score : undefined;

    this.round = new MatchRound(init.round);

    this.referee = init.referee;
    this.spectators = init.spectators;

    if (init.league_info) {
      this.league_info = init.league_info;
    }
    if (init.sportradar) {
      this.sportradar = init.sportradar;
    }
  }

  setStatus(value: string) {
    this.status = value;
    this.statusObject = MatchStatus.fromValue(value);
  }

  getIsRunningByTimeStamps(): boolean {
    const now = new Date();
    if ((now.getTime() > this.timestamps.start && now.getTime() < this.timestamps.end)) {
      return true;
    }
    return false;
  }

  // TODO, here for legacy reasons, check usages and replace with isPastWithNormal outcome and/or isPastWithSpecialOutcome
  isPast(): boolean {
    return PostGameAndCancelledPhases.includes(this.status as EventStatus);
  }

  isPastWithNormalOutcome(): boolean {
    return this.statusObject === MatchStatus.POSTGAME || this.statusObject === MatchStatus.ENDED;
  }

  isPastWithSpecialOutcome(): boolean {
    switch (this.statusObject) {
      case MatchStatus.CANCEL:
      case MatchStatus.CANCELLED:
      case MatchStatus.ABORT:
      case MatchStatus.ABORTED:
        return true;
      default:
        return false;
    }
  }

  isCancelled(): boolean {
    switch (this.statusObject) {
      case MatchStatus.CANCEL:
      case MatchStatus.CANCELLED:
        return true;
      default:
        return false;
    }
  }

  isAborted(): boolean {
    switch (this.statusObject) {
      case MatchStatus.ABORT:
      case MatchStatus.ABORTED:
        return true;
      default:
        return false;
    }
  }

  isCurrent(useTimestampFallback = true): boolean {
    switch (this.statusObject) {
      case MatchStatus.FH:
      case MatchStatus.FH_START:
      case MatchStatus.HTPAUSE:
      case MatchStatus.FH_PAUSE:
      case MatchStatus.HT_RUNNING:
      case MatchStatus.SH:
      case MatchStatus.SH_START:
      case MatchStatus.SHPAUSE:
      case MatchStatus.SH_PAUSE:
      case MatchStatus.OTFH:
      case MatchStatus.ET_FH_START:
      case MatchStatus.OTPAUSE:
      case MatchStatus.ET_FH_PAUSE:
      case MatchStatus.OTSH:
      case MatchStatus.ET_SH_START:
      case MatchStatus.PS:
      case MatchStatus.PENALTY_SHOOTOUT:
        return true;
      default:
        return useTimestampFallback && this.getIsRunningByTimeStamps() && this.isPast() === false;
    }
  }

  isFuture(): Boolean {
    switch (this.statusObject) {
      case MatchStatus.UPCOMING:
      case MatchStatus.PREGAME:
        return true;
    }
    return false;
  }

  isCurrentOrPast(useTimestampFallback = true): boolean {
    return this.isCurrent(useTimestampFallback) || this.isPast();
  }

  isHalfTime(): boolean {
    switch (this.statusObject) {
      case MatchStatus.HTPAUSE:
      case MatchStatus.FH_PAUSE:
        return true;
    }
    return false;
  }

  isPauseOtherThanHalfTime(): boolean {
    switch (this.statusObject) {
      case MatchStatus.OTPAUSE:
      case MatchStatus.ET_FH_PAUSE:
        return true;
    }
    return false;
  }

  wasStartedButReporterLeft(): Boolean {
    if (this.statusObject === MatchStatus.HT_RUNNING) {
      return true;
    }

    return false;
  }

  wasNotStarted(): Boolean {
    return this.statusObject === MatchStatus.PREGAME || this.statusObject === MatchStatus.RUNNING;
  }

  getTimestamps() {
    return this.timestamps;
  }

  wasYesterdayOrPrevious() {
    const today = new Date();
    today.setHours(0);
    today.setMinutes(0);
    today.setSeconds(0);
    today.setMilliseconds(0);

    if (this.timestamps.start < today.getTime()) {
      return true;
    } else {
      return false;
    }
  }

  hadOvertime() {
    if (this.scores.overtime != null && this.scores.overtime.secondhalf != null && this.scores.overtime.secondhalf.end != null) {
      if (this.scores.overtime.shootout == null || this.scores.overtime.shootout.end == null) {
        return true;
      }
    }
    return false;
  }

  hadKickOff() {
    if (this.scores.overtime != null && this.scores.overtime.secondhalf != null && this.scores.overtime.secondhalf.end != null) {
      if (this.scores.overtime.shootout != null && this.scores.overtime.shootout.end != null) {
        return true;
      }
    }
    return false;
  }

  getDateFormatted(todayAsString: Boolean = false, options: any = { year: 'numeric', month: '2-digit', day: '2-digit' }): string {
    const date  = new Date(this.timestamps.start);

    if (todayAsString) {
      if (DateService.datesAreOnSameDay(new Date(this.timestamps.start), new Date())) {
        return 'Heute';
      }
    }

    return date.toLocaleDateString('de-DE', options);
  }

  getTimeFormatted(options: any = { hour: '2-digit', minute: '2-digit' }): string {
    const date  = new Date(this.timestamps.start);
    return date.toLocaleTimeString('de-DE', options);
  }

  toString(): string {
    return `id: ${ this.id }, date: ${ this.getDateFormatted() }, league: ${ this.league }`;
  }

  hasVideos() {
    return !!this.videos.livestreamURL || !!this.videos.highlightsURL || !!this.videos.tickerURL;
  }

  getRoundUIName(): string {
    if (this.round == null || this.round.ui_name == null) {
      return null;
    }
    return this.round.ui_name;
  }

  moderatorsOccupied(): boolean {
    if (this.fanModeratorHome.moderatorIsFree() || this.fanModeratorAway.moderatorIsFree()) {
      return false;
    }
    return true;
  }

  isAtLeastOneHourBeforeMatchStart(): boolean {
      const oneHourFromNow = new Date();
      oneHourFromNow.setHours(oneHourFromNow.getHours() + 1);
      const msecsPerHour = 1000 * 60 * 60;
      const timeDiff = oneHourFromNow.getTime() - this.timestamps.start;
      return timeDiff < 0 && Math.abs(timeDiff) <= msecsPerHour;
    }

  hasResults(): boolean {
    return this.homeScore !== undefined && this.awayScore !== undefined;
  }

  getFirstHalfScore(): string {
    if (this.scores && this.scores.regular.firsthalf && this.scores.regular.firsthalf.end) {
      const hasValidScore = this.scores.regular.firsthalf.end.indexOf('-1') === -1;
      if (hasValidScore) {
        return this.scores.regular.firsthalf.end;
      }
    }

    return null;
  }

  getMatchStatusString(): string {
    if (this.isCurrent()) {
      if (this.wasStartedButReporterLeft() || this.wasNotStarted()) {
        return 'Kein Ticker';
      }

      if (this.isCancelled()) {
        return 'Abgesagt';
      } else if (this.isAborted()) {
        return 'Spielabbruch';
      } else if (this.isHalfTime()) {
        return 'Halbzeit';
      } else if (this.isPauseOtherThanHalfTime()) {
        return 'Pause';
      } else {
        return 'Live';
      }
    }

    if (this.isFuture()) {
      return 'Zukünftig';
    }

    if (this.isPast()) {
      if (this.isPastWithSpecialOutcome()) {
        if (this.isCancelled()) {
          return 'Abgesagt';
        } else if (this.isAborted()) {
          return 'Spielabbruch';
        }
      }

      if (this.hadOvertime()) {
        return 'Verlängerung';
      } else if (this.hadKickOff()) {
        return 'Elfmeter';
      } else {
        if (this.hasResults()) {
          return 'Endstand';
        }
      }
    }

    return null;
  }

  getRefereeName(): string {
    if (!this.referee) {
      return null;
    }

    let name = '';
    if (this.referee.firstname) {
      name += this.referee.firstname;
    }
    if (this.referee.lastname) {
      if (name) {
        name += ' ' + this.referee.lastname;
      } else {
        name = this.referee.lastname;
      }
    }

    return name;
  }

  fanModerationIsOpenForTicker(): boolean {
    if (this.fanModeratorHome == null || this.fanModeratorAway == null) {
      return false;
    }
    if (!FanModerator.openForTicker(this.fanModeratorHome, this) && !FanModerator.openForTicker(this.fanModeratorAway, this)) {
      return false;
    } else {
      return true;
    }
  }

  hasAtLeastOneTeamModerator(): boolean {
    return this.teams.home.hasTeamModerator || this.teams.away.hasTeamModerator;
  }

  fanModerationIsImmediate(): boolean {
    if (this.fanModeratorHome == null || this.fanModeratorAway == null) {
      return false;
    }
    if (this.fanModeratorHome.status === FanModeratorStatusType.IMMEDIATE.value ||
        this.fanModeratorAway.status === FanModeratorStatusType.IMMEDIATE.value) {
      return true;
    } else {
      return false;
    }
  }


  getAsEntity(): Entity {
    return new Entity({
      id: this.id,
      title: `${this.teams?.home?.name} vs. ${this.teams?.away?.name}`,
      type: EntityType.Event,
      leagueName: this.league_info?.league_name,
      leagueId: this.leagueid,
      paywall: this.streaming_info?.paywall,
      performanceLevel: this.league_info.league_performance_level,
      gender: this.sex,
      phase: this.status,
      live: this.isCurrent(),
    });
  }
}
